/**
 * Tables de nomenclatures canalisations : Ultra Mesure
 *
 * Données fournies par Renaud Merle (mars 2026).
 * Sources : Excel épaisseurs tubes, PDF catalogues COMAP/Steelpres/Solyro/PAM,
 *           normes EN10255, NFA 49-141, EN10216.1, EN 545.
 *
 * Chaque entrée : [diametreExt, dn, epaisseur]
 * Pour la fonte : [dn, diametreExt, epaisseur]
 */

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

/** Entrée de table tube : [∅ ext (mm), DN, épaisseur (mm)] */
export type TubeEntry = readonly [number, number, number];

/** Entrée fonte : [DN, DE (mm), épaisseur (mm)] */
export type FonteEntry = readonly [number, number, number];

// ─────────────────────────────────────────────
// ACIER NOIR / ACIER GALVANISÉ (mêmes normes)
// ─────────────────────────────────────────────

/** Tarif 1 : EN10255, tubes soudés */
export const ACIER_T1: TubeEntry[] = [
  [10.2, 6, 1.8],
  [13.5, 8, 2.0],
  [17.2, 10, 2.0],
  [21.3, 15, 2.3],
  [26.9, 20, 2.3],
  [33.7, 25, 2.9],
  [42.4, 32, 2.9],
  [48.3, 40, 2.9],
  [60.3, 50, 3.2],
  [76.1, 65, 3.2],
  [88.9, 80, 3.2],
  [101.6, 90, 3.6],
  [114.3, 100, 3.6],
];

/** Tarif 19 : NFA 49-141, tubes soudés */
export const ACIER_T19: TubeEntry[] = [
  [26.9, 20, 2.0],
  [30.0, 25, 2.3],
  [33.7, 25, 2.3],
  [38.0, 32, 2.6],
  [42.4, 32, 2.6],
  [44.5, 32, 2.6],
  [48.3, 40, 2.6],
  [54.0, 40, 2.6],
  [57.0, 40, 2.9],
  [60.3, 50, 2.9],
  [76.1, 65, 2.9],
  [88.9, 80, 3.2],
  [108.0, 100, 3.6],
  [114.3, 100, 3.6],
  [133.0, 125, 4.0],
  [139.7, 125, 4.0],
  [159.0, 150, 4.5],
  [168.3, 150, 4.5],
];

/** Tarif 3 : EN10255, tubes sans soudure */
export const ACIER_T3: TubeEntry[] = [
  [10.2, 6, 2.0],
  [13.5, 8, 2.3],
  [17.2, 10, 2.3],
  [21.3, 15, 2.6],
  [26.9, 20, 2.6],
  [33.7, 25, 3.2],
  [42.4, 32, 3.2],
  [48.3, 40, 3.2],
  [60.3, 50, 3.6],
  [76.1, 65, 3.6],
  [88.9, 80, 4.0],
  [101.6, 90, 4.0],
  [114.3, 100, 4.5],
  [139.7, 125, 4.5],
  [165.1, 150, 4.5],
];

/** Tarif 10 : EN10216.1, tubes sans soudure */
export const ACIER_T10: TubeEntry[] = [
  [26.9, 20, 2.3],
  [33.7, 25, 2.3],
  [38.0, 25, 2.6],
  [42.4, 32, 2.6],
  [44.5, 32, 2.6],
  [48.3, 40, 2.6],
  [54.0, 40, 2.6],
  [57.0, 40, 2.9],
  [60.3, 50, 2.9],
  [70.0, 50, 2.9],
  [76.1, 65, 2.9],
  [88.9, 80, 3.2],
  [101.6, 90, 3.6],
  [108.0, 100, 3.6],
  [114.3, 100, 3.6],
  [133.0, 125, 4.0],
  [139.7, 125, 4.0],
  [159.0, 150, 4.5],
  [168.3, 150, 4.5],
  [193.7, 150, 5.6],
  [219.1, 200, 6.3],
  [244.5, 200, 6.3],
  [273.0, 250, 6.3],
  [323.9, 300, 7.1],
  [355.6, 350, 8.0],
  [406.4, 400, 8.8],
  [457.0, 450, 10.0],
  [508.0, 500, 11.0],
  [559.0, 550, 12.5],
  [610.0, 600, 12.5],
];

/** Map nom de tarif → table */
export const ACIER_TARIFS: Record<string, TubeEntry[]> = {
  T1: ACIER_T1,
  T19: ACIER_T19,
  T3: ACIER_T3,
  T10: ACIER_T10,
};

// ─────────────────────────────────────────────
// ACIER ÉLECTROZINGUÉ (COMAP XPress)
// ─────────────────────────────────────────────

export const ACIER_ELECTROZINGUE: TubeEntry[] = [
  [12, 10, 1.2],
  [15, 12, 1.2],
  [18, 15, 1.2],
  [22, 20, 1.5],
  [28, 25, 1.5],
  [35, 32, 1.5],
  [42, 40, 1.5],
  [54, 50, 1.5],
  [66.7, 60, 1.5],
  [76.1, 65, 2.0],
  [88.9, 80, 2.0],
  [108, 100, 2.0],
];

// ─────────────────────────────────────────────
// ACIER INOX (ISO GAZ : Solyro)
// Épaisseur NON auto-remplie (Renaud la lit sur le tuyau)
// On fournit uniquement le mapping ∅ ext → DN
// ─────────────────────────────────────────────

/** Inox ISO GAZ : [∅ ext, DN, 0] : épaisseur à 0 car non applicable */
export const ACIER_INOX: TubeEntry[] = [
  [17.2, 10, 0],
  [21.3, 15, 0],
  [26.9, 20, 0],
  [33.7, 25, 0],
  [42.4, 32, 0],
  [48.3, 40, 0],
  [60.3, 50, 0],
  [76.1, 65, 0],
  [88.9, 80, 0],
  [114.3, 100, 0],
  [139.7, 125, 0],
  [168.3, 150, 0],
  [219.1, 200, 0],
  [273.0, 250, 0],
  [323.9, 300, 0],
  [355.6, 350, 0],
  [406.4, 400, 0],
];

// ─────────────────────────────────────────────
// CUIVRE (NF EN 1057)
// Pas de DN : désignation par ∅ ext directement
// Épaisseur = 1 mm pour tous les ∅ courants en chauffage
// ─────────────────────────────────────────────

/** Cuivre : [∅ ext, DN, épaisseur] : DN = ∅ext - 2×ép */
export const CUIVRE: TubeEntry[] = [
  [12, 10, 1.0],
  [14, 12, 1.0],
  [15, 13, 1.0],
  [16, 14, 1.0],
  [18, 16, 1.0],
  [22, 20, 1.0],
  [25, 23, 1.0],
  [28, 26, 1.0],
  [35, 33, 1.0],
  [40, 38, 1.0],
  [42, 40, 1.0],
  [54, 52, 1.0],
];

// ─────────────────────────────────────────────
// FONTE SMU (récente : cotes de construction)
// ─────────────────────────────────────────────

/** Fonte SMU : [DN, DE, épaisseur] */
export const FONTE_SMU: FonteEntry[] = [
  [50, 58, 3.5],
  [70, 78, 3.5],
  [75, 75, 3.5],
  [80, 83, 3.5],
  [100, 110, 3.5],
  [125, 135, 4.0],
  [150, 160, 4.0],
  [200, 210, 5.0],
  [250, 274, 5.5],
  [300, 326, 6.0],
];

// ─────────────────────────────────────────────
// FONTE SME RÉCENTE (EN 545, classe K9 nominale)
// ─────────────────────────────────────────────

/** Fonte SME récente (K9) : [DN, DE (≈), épaisseur nominale K9] */
export const FONTE_SME_RECENTE: FonteEntry[] = [
  [60, 77, 6.0],
  [80, 98, 6.0],
  [100, 118, 6.0],
  [125, 144, 6.0],
  [150, 170, 6.0],
  [200, 222, 6.3],
  [250, 274, 6.8],
  [300, 326, 7.2],
  [350, 378, 7.7],
  [400, 429, 8.1],
  [450, 480, 8.6],
  [500, 532, 9.0],
  [600, 635, 9.9],
  [700, 738, 10.8],
  [800, 842, 11.7],
  [900, 945, 12.6],
  [1000, 1048, 13.5],
];

// ─────────────────────────────────────────────
// FONTE SME VIEILLE (EX cylindrique, NF A 48-607)
// ─────────────────────────────────────────────

/** Fonte SME vieille (EX cylindrique) : [DN, DE, épaisseur] */
export const FONTE_SME_VIEILLE: FonteEntry[] = [
  [60, 77, 6.8],
  [80, 98, 7.2],
  [100, 118, 7.5],
  [125, 144, 7.9],
  [150, 170, 8.3],
  [175, 196, 8.8],
  [200, 222, 9.2],
  [250, 274, 10.0],
  [300, 326, 10.8],
  [350, 378, 11.7],
  [400, 429, 12.5],
  [450, 480, 13.3],
  [500, 532, 14.2],
  [600, 635, 15.8],
];

// ─────────────────────────────────────────────
// Fonctions de lookup
// ─────────────────────────────────────────────

/** Tolérance pour le matching ∅ ext : ±5% ou ±1mm (le plus grand des deux) */
function isCloseEnough(measured: number, reference: number): boolean {
  const tolerance = Math.max(1, reference * 0.05);
  return Math.abs(measured - reference) <= tolerance;
}

/** Trouve l'entrée la plus proche dans une table de tubes */
function findClosestTubeEntry(
  diametreExt: number,
  table: TubeEntry[]
): TubeEntry | null {
  let best: TubeEntry | null = null;
  let bestDiff = Infinity;

  for (const entry of table) {
    const diff = Math.abs(entry[0] - diametreExt);
    if (diff < bestDiff && isCloseEnough(diametreExt, entry[0])) {
      best = entry;
      bestDiff = diff;
    }
  }

  return best;
}

/** Trouve l'entrée fonte par DN (match exact) ou par DE (approché) */
function findFonteEntry(
  table: FonteEntry[],
  dn: number | null,
  diametreExt: number | null
): FonteEntry | null {
  // Priorité au DN (match exact)
  if (dn !== null) {
    const byDn = table.find((e) => e[0] === dn);
    if (byDn) return byDn;
  }

  // Fallback : chercher par DE (∅ ext approché)
  if (diametreExt !== null) {
    let best: FonteEntry | null = null;
    let bestDiff = Infinity;
    for (const entry of table) {
      const diff = Math.abs(entry[1] - diametreExt);
      if (diff < bestDiff && isCloseEnough(diametreExt, entry[1])) {
        best = entry;
        bestDiff = diff;
      }
    }
    return best;
  }

  return null;
}

// ─────────────────────────────────────────────
// Diamètres disponibles par matériau
// ─────────────────────────────────────────────

/**
 * Retourne la liste triée des ∅ ext normés pour un matériau donné.
 * Pour acier noir/galva : filtre par tarification si fournie, sinon toutes.
 * Pour la fonte : retourne les DE (colonne [1]).
 */
export function getAvailableDiameters(
  materiau: string,
  tarification?: string[]
): number[] {
  const diameters = new Set<number>();

  if (materiau === "Cuivre") {
    CUIVRE.forEach((e) => diameters.add(e[0]));
  } else if (materiau === "Acier électrozingué") {
    ACIER_ELECTROZINGUE.forEach((e) => diameters.add(e[0]));
  } else if (materiau === "Acier inoxydable") {
    ACIER_INOX.forEach((e) => diameters.add(e[0]));
  } else if (materiau === "Acier noir" || materiau === "Acier galvanisé") {
    if (tarification && tarification.length > 0) {
      for (const tarif of tarification) {
        const table = ACIER_TARIFS[tarif];
        if (table) table.forEach((e) => diameters.add(e[0]));
      }
    } else {
      Object.values(ACIER_TARIFS).forEach((table) =>
        table.forEach((e) => diameters.add(e[0]))
      );
    }
  } else if (materiau === "Fonte") {
    [FONTE_SMU, FONTE_SME_RECENTE, FONTE_SME_VIEILLE].forEach((table) =>
      table.forEach((e) => diameters.add(e[1]))
    );
  }

  return [...diameters].sort((a, b) => a - b);
}

// ─────────────────────────────────────────────
// API publique
// ─────────────────────────────────────────────

export interface LookupOptions {
  diametreExt?: number | null;
  tarification?: string[];
  fonteAge?: string;
  fonteType?: string;
}

/**
 * Trouve le DN à partir du ∅ ext et du matériau.
 * Cherche dans la table appropriée au matériau.
 * Pour acier noir/galva, cherche dans toutes les tables tarif.
 */
export function lookupDn(
  diametreExt: number | null,
  materiau: string,
  tarification?: string[]
): number | null {
  if (diametreExt === null || diametreExt <= 0) return null;

  // Cuivre : DN par ∅ ext
  if (materiau === "Cuivre") {
    const entry = findClosestTubeEntry(diametreExt, CUIVRE);
    return entry ? entry[1] : null;
  }

  // Acier électrozingué
  if (materiau === "Acier électrozingué") {
    const entry = findClosestTubeEntry(diametreExt, ACIER_ELECTROZINGUE);
    return entry ? entry[1] : null;
  }

  // Acier inox
  if (materiau === "Acier inoxydable") {
    const entry = findClosestTubeEntry(diametreExt, ACIER_INOX);
    return entry ? entry[1] : null;
  }

  // Acier noir / Acier galvanisé
  if (materiau === "Acier noir" || materiau === "Acier galvanisé") {
    // Si un tarif est sélectionné, chercher d'abord dedans
    if (tarification && tarification.length > 0) {
      for (const tarif of tarification) {
        const table = ACIER_TARIFS[tarif];
        if (table) {
          const entry = findClosestTubeEntry(diametreExt, table);
          if (entry) return entry[1];
        }
      }
    }
    // Sinon chercher dans tous les tarifs
    for (const table of Object.values(ACIER_TARIFS)) {
      const entry = findClosestTubeEntry(diametreExt, table);
      if (entry) return entry[1];
    }
    return null;
  }

  // Fonte : lookup par DE
  if (materiau === "Fonte") {
    // Chercher dans toutes les tables fonte
    for (const table of [FONTE_SMU, FONTE_SME_RECENTE, FONTE_SME_VIEILLE]) {
      for (const entry of table) {
        if (isCloseEnough(diametreExt, entry[1])) {
          return entry[0]; // DN
        }
      }
    }
    return null;
  }

  return null;
}

/**
 * Trouve l'épaisseur d'origine à partir du matériau et du contexte.
 * - Acier noir/galva : besoin de tarification + ∅ ext
 * - Acier électrozingué : par ∅ ext
 * - Cuivre : par ∅ ext (toujours 1mm)
 * - Inox : retourne null (Renaud lit l'épaisseur sur le tuyau)
 * - Fonte : par DN + âge + type (SME/SMU)
 */
export function lookupEpaisseur(
  materiau: string,
  dn: number | null,
  options?: LookupOptions
): number | null {
  const dExt = options?.diametreExt ?? null;

  // Cuivre : épaisseur par ∅ ext
  if (materiau === "Cuivre") {
    if (dExt === null) return null;
    const entry = findClosestTubeEntry(dExt, CUIVRE);
    return entry ? entry[2] : null;
  }

  // Acier inoxydable : pas d'auto-fill (Renaud lit sur le tuyau)
  if (materiau === "Acier inoxydable") return null;

  // Acier électrozingué : par ∅ ext
  if (materiau === "Acier électrozingué") {
    if (dExt === null) return null;
    const entry = findClosestTubeEntry(dExt, ACIER_ELECTROZINGUE);
    return entry ? entry[2] : null;
  }

  // Acier noir / Acier galvanisé : par tarification + ∅ ext
  if (materiau === "Acier noir" || materiau === "Acier galvanisé") {
    if (dExt === null) return null;
    const tarifs = options?.tarification ?? [];

    // Chercher dans les tarifs sélectionnés
    if (tarifs.length > 0) {
      for (const tarif of tarifs) {
        const table = ACIER_TARIFS[tarif];
        if (table) {
          const entry = findClosestTubeEntry(dExt, table);
          if (entry) return entry[2];
        }
      }
    }

    // Si "Hors nomenclature" ou pas de tarif → pas d'auto-fill
    return null;
  }

  // Fonte : par DN + âge + type.
  // Pas d'auto-fill tant que l'âge ET le type ne sont pas choisis (arbitrage
  // Renaud, juillet 2026) : l'ancien fallback silencieux sur SMU imposait
  // 3,5 mm sur presque tous les DN courants, sans que le choix SME/SMU
  // ultérieur ne semble avoir d'effet.
  if (materiau === "Fonte") {
    const fonteType = options?.fonteType;
    const fonteAge = options?.fonteAge;

    if (!fonteType || !fonteAge) return null;

    // SMU : toujours la même table (l'âge n'influe pas, mais on l'exige
    // quand même pour un comportement prévisible annoncé à Renaud)
    if (fonteType === "SMU") {
      const entry = findFonteEntry(FONTE_SMU, dn, dExt);
      return entry ? entry[2] : null;
    }

    // SME : dépend de l'âge
    if (fonteType === "SME") {
      const table = fonteAge === "Vieux" ? FONTE_SME_VIEILLE : FONTE_SME_RECENTE;
      const entry = findFonteEntry(table, dn, dExt);
      return entry ? entry[2] : null;
    }

    return null;
  }

  return null;
}
